import React, { forwardRef, useImperativeHandle, useReducer, useEffect, useState, useContext } from "react";
import { TextField, Text, ITextFieldStyles, ILabelStyleProps, ILabelStyles, PrimaryButton } from "@fluentui/react";
import InputFileButton from "../../baseComponents/InputFileButton";
import { RuntimeError, FatalError } from "../../services/ErrorHelpers";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { RequireAtLeastOne } from "../../services/TypeUtil";

import BatchWorker, { IBatchSchema } from "../../workers/batchWorker";
import { Observable, throwError, Observer } from "rxjs";
import { css } from "@emotion/css";
import { IAttachment } from "../../schemas/SLAttachment";
import { getAttachmentDownloadHandler } from "../Attachments";
import { getDescriptionErrorMessage, getBatchNameErrorMessage, getPurposeErrorMessage } from "./utilities";
import { UserContext } from "../../contexts/UserContext";
import { LoggingContext } from "../../contexts/LoggingContext";
import { SLLoadingStatus } from "../../utilities/utilities";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { ISLBatchResponse } from "./SLBatchSchemas";

const headerStyles = css`
  & .batch-file-input {
    padding-bottom: 8px;
  }
  & .batch-description-margin {
    margin-left: 8px;
  }
  & .batch-browse-button {
    float: right;
  }
  & .title-color {
    color: black;
  }
`;

function getStyles(): Partial<ITextFieldStyles> {
  return {
    subComponentStyles: {
      label: getLabelStyles
    }
  };
}

function getLabelStyles(props: ILabelStyleProps): ILabelStyles {
  const { required } = props;
  return {
    root: [
      required && {
        color: props.theme.palette.black
      }
    ]
  };
}

export interface ISLBatchHeaderGetter {
  result: ISLBatchHeader;
}

export interface ISLBatchHeaderRef {
  getHeader: () => ISLBatchHeaderGetter;
  setHeader: (theHeader: ISLBatchHeader) => void;
  reset: () => void;
}

const readFileStream = (file: File): Observable<string> => {
  if (!(file instanceof File)) {
    return throwError(new Error("`file` must be an instance of File."));
  }

  return Observable.create((obs: Observer<ArrayBuffer>) => {
    const reader = new FileReader();

    reader.onerror = (err) => obs.error(err);
    reader.onabort = (err) => obs.error(err);
    reader.onload = (e) => {
      if (!e.target) {
        return obs.error(new Error(""));
      }
      return obs.next(e.target.result as ArrayBuffer);
    };
    reader.onloadend = () => obs.complete();

    return reader.readAsArrayBuffer(file);
  });
};

const getBatchData = (worker: Worker, file: File): Promise<IBatchSchema> => {
  return new Promise((resolve, reject) => {
    const fileStream = readFileStream(file);
    fileStream.subscribe((data) => {
      worker.onmessage = (e: MessageEvent) => {
        switch (e.data.t) {
          case "ready":
            break;
          case "e":
            reject(e.data.d);
            break;
          case "xlsx":
            resolve(e.data.d as IBatchSchema);
            break;
        }
      };
      worker.postMessage({ d: data });
    });
  });
};

export interface ISLBatchHeader {
  name: string;
  description: string;
  purpose: string;
  file: File | null;
  batchFileInfo: IAttachment | null;
  refGUID: string;
}

const initialBatchState: ISLBatchHeader = {
  purpose: "",
  description: "",
  name: "",
  file: null,
  batchFileInfo: null,
  refGUID: ""
};

type BatchHeaderReducer = (state: ISLBatchHeader, changes: RequireAtLeastOne<ISLBatchHeader>) => ISLBatchHeader;

enum ERRORS {
  NAME,
  DESCRIPTION,
  PURPOSE,
  CLEAR
}

const initialErrorState = {
  name: "",
  description: "",
  purpose: ""
};

const uiCheckForErrors = (excelContent: IBatchSchema) => {
  const msgs: string[] = [];
  if (excelContent.emptyLineItems) {
    msgs.push("At least 2 line items are required.");
  }
  const descriptionError = getDescriptionErrorMessage(excelContent.batchDescription);
  if (descriptionError !== undefined) {
    msgs.push(descriptionError);
  }
  const batchNameError = getBatchNameErrorMessage(excelContent.batchName);
  if (batchNameError !== undefined) {
    msgs.push(batchNameError);
  }
  const purposeError = getPurposeErrorMessage(excelContent.purpose);
  if (purposeError !== undefined) {
    msgs.push(purposeError);
  }
  // props.uiChecks(msgs);
  return msgs.length !== 0;
};

function batchResponseToHeader(batchEntry: ISLBatchResponse): ISLBatchHeader {
  return {
    file: null,
    batchFileInfo: batchEntry.batchFileInfo,
    name: batchEntry.batchName || "",
    description: batchEntry.batchDescription || "",
    purpose: batchEntry.purpose || "",
    refGUID: batchEntry.refGUID || ""
  };
}

const SLBatchHeader = forwardRef((props, ref: React.Ref<ISLBatchHeaderRef>) => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please setup ApplicationInsights Provider for SLBatchHeader");
  }
  const { accessToken } = useContext(UserContext);
  const entryContext = useContext(SLCreateSLBatchContext);
  const [state, setState] = useReducer<BatchHeaderReducer>(
    (previousState, changes) => ({ ...previousState, ...changes }),
    initialBatchState
  );
  const [disableViewFileButton, setDisableViewFileButton] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [worker, setWorker] = useState<Worker | null>(null);

  const [errors, setErrors] = useReducer((previousErrors: typeof initialErrorState, type: ERRORS) => {
    const newErrors = { ...previousErrors };
    switch (type) {
      case ERRORS.NAME:
        const batchNameError = getBatchNameErrorMessage(state.name);
        if (batchNameError !== undefined) {
          newErrors.name = batchNameError;
        }
        break;
      case ERRORS.DESCRIPTION:
        const descriptionError = getDescriptionErrorMessage(state.description);
        if (descriptionError !== undefined) {
          newErrors.description = descriptionError;
        }
        break;
      case ERRORS.PURPOSE:
        const purposeError = getDescriptionErrorMessage(state.purpose);
        if (purposeError !== undefined) {
          newErrors.purpose = purposeError;
        }
        break;
      case ERRORS.CLEAR:
        return initialErrorState;
    }
    return newErrors;
  }, initialErrorState);

  useEffect(() => {
    const noErrorsFound = Object.keys(errors).every((key: string) => errors[key as keyof typeof errors] === "");
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      entryContext.setErrors({
        collectorName: "Header",
        value: !noErrorsFound
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (state.refGUID !== null && state.refGUID !== "") {
      setDisableViewFileButton(false);
    }
  }, [state.refGUID]);

  useEffect(() => {
    if (loadingStatus === SLLoadingStatus.Idle) {
      return;
    }
    if (state.name !== null) {
      setErrors(ERRORS.NAME);
    }
    if (state.description !== null) {
      setErrors(ERRORS.DESCRIPTION);
    }
    if (state.purpose !== null) {
      setErrors(ERRORS.PURPOSE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.name, state.description, state.purpose]);

  useEffect(() => {
    if (!worker) {
      setWorker(new BatchWorker());
    }
    return () => {
      setWorker(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (entryContext.entry) {
        const theHeader = batchResponseToHeader(entryContext.entry as ISLBatchResponse);
        setState(theHeader);
      } else {
        setState(initialBatchState);
      }
      setLoadingStatus(SLLoadingStatus.Resolved);
    } else if (entryContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingStatus(SLLoadingStatus.Rejected);
    } else {
      setLoadingStatus(SLLoadingStatus.Pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryContext.loadingStatus, entryContext.entry]);

  useImperativeHandle(ref, () => ({
    getHeader() {
      return {
        result: state
      };
    },
    setHeader(theHeader) {
      const newHeader = Object.keys(initialBatchState).reduce((ctr, k: string) => {
        const key = k as keyof typeof theHeader;
        if (key === "batchFileInfo") {
          ctr[key] = theHeader[key];
        } else if (key === "file") {
          ctr[key] = theHeader[key];
        } else if (theHeader[key] === null) {
          ctr[key] = "";
        } else {
          ctr[key] = theHeader[key];
        }
        return ctr;
      }, {} as typeof theHeader);
      setState(newHeader);
    },
    reset() {
      setState(initialBatchState);
      setErrors(ERRORS.CLEAR);
    }
  }));

  const onFileSelected = async (files: File[]) => {
    setErrors(ERRORS.CLEAR);
    if (files.length > 1) {
      throw new RuntimeError("Batch File Input should not allow multiple files.");
    }
    if (worker) {
      try {
        setLoadingStatus(SLLoadingStatus.Pending);
        const batchInfo = await getBatchData(worker, files[0]);
        uiCheckForErrors(batchInfo);
        setState({
          file: files[0],
          description: batchInfo.batchDescription,
          name: batchInfo.batchName,
          purpose: batchInfo.purpose
        });
        setLoadingStatus(SLLoadingStatus.Resolved);
      } catch (e) {
        console.error(e);
        appInsights.trackException({ exception: e });
        setLoadingStatus(SLLoadingStatus.Rejected);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const getFileName = () => {
    if (state.file && state.file.name) return state.file.name;
    if (state.batchFileInfo && state.batchFileInfo.fileName) return state.batchFileInfo.fileName;
    return "";
  };

  const getBlobName = () => {
    if (state.batchFileInfo && state.batchFileInfo.blobName) return state.batchFileInfo.blobName;
    return "";
  };

  if (loadingStatus === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading Batch Header." />;
  } else if (loadingStatus === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingStatus === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, #727272)",
          textAlign: "center"
        }}
      >
        Error while loading Header Data. Please ensure that the batch template is used for the posting and all the
        required details are listed in the file.
      </Text>
    );
  }

  return (
    <React.Fragment>
      <div className={headerStyles}>
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-lg4 title-color">
              <TextField
                label="Batch Name"
                name="batchName"
                value={state.name || ""}
                onChange={noop}
                disabled
                required
                errorMessage={errors.name}
                styles={getStyles}
              />
            </div>
            <div className="ms-Grid-col ms-lg4">
              <div className="ms-Grid-row batch-file-input">
                <TextField
                  label="Batch File"
                  name="batchFile"
                  value={getFileName()}
                  onChange={noop}
                  disabled
                  required
                  styles={getStyles}
                />
              </div>
              <div className="ms-Grid-row batch-browse-button">
                <PrimaryButton
                  text="View File"
                  disabled={disableViewFileButton}
                  onClick={getAttachmentDownloadHandler(getBlobName(), getFileName(), accessToken)}
                />
                &nbsp;
                <InputFileButton text="Browse" onChange={onFileSelected} accept=".xlsx" allowMultipleFiles={false} />
              </div>
            </div>
          </div>
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-lg4 batch-description-margin title-color">
              <TextField
                label="Batch Description"
                name="batchDescription"
                value={state.description || ""}
                onChange={noop}
                disabled
                multiline
                autoAdjustHeight
                rows={3}
                required
                errorMessage={errors.description}
                styles={getStyles}
              />
            </div>
            <div className="ms-Grid-col ms-lg4 ">
              <TextField
                label="Purpose"
                name="purpose"
                value={state.purpose || ""}
                onChange={noop}
                multiline
                disabled
                autoAdjustHeight
                rows={3}
                required
                errorMessage={errors.purpose}
                styles={getStyles}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

SLBatchHeader.displayName = "SLBatchHeader";

export default SLBatchHeader;
